import _ from 'lodash-extended';
import { mapState, mapActions } from 'vuex';
import gridMixin from '../grid.mixin';
import {
  getVisualAnchorTimes,
  findVisualAnchorPoint,
  ContinuousTimeout,
  assignDurations,
  isNumber,
  CursorManager,
} from '../../api/tapi';
import {
  createOverlayFromExternalAsset,
  VisualGhostMode,
} from '../../api/model';
import { TimePoint } from '../../api/grid';
import { isTimeInsideTrack } from '../../api/tapi';
import tmBus from '../../api/tmBus';
import fg from '#/libs/FrameGrid';

export default {
  mixins: [gridMixin],

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      SCENE_EDIT_TIMEOUT: (state) => state.CONST.SCENE_EDIT_TIMEOUT,
      dndEscort: (state) => state.dndEscort,
      sceneEscort: (state) => state.sceneEscort,
      useSceneEdit: (state) => state.USE.sceneEdit,
    }),

    moveObj() {
      return {
        enabled: this.$data.dragEnabled,
        distance2trigger: this.CONST.DND_SENSITIVITY.DEFAULT,
        onMoveStart: this.$_onMoveStart,
        onMove: this.$_onMove,
        onMoveEnd: this.$_onMoveEnd,
      };
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'SET_MODE',
      'SET_DND_ESCORT',
      'SET_SECONDHAND',
      'INSERT_VISUAL_ASSET',
      'REPLACE_VISUAL_TRACK',
      'SCENE_EDIT_MODE_ENTER',
      'SCENE_EDIT_MODE_LEAVE',
      'SELECT_OVERLAY',
      'SET_SCENE_ESCORT',
      'MANAGED_AUDIO_INSERT_EXIT',
      'AUDIO_MULTISELECT_EXIT',
    ]),

    $_onMoveStart(state) {
      if (this.MODE_OPTIONAL_AUDIO_INSERT) {
        this.MANAGED_AUDIO_INSERT_EXIT();
      }
      if (this.MODE_AUDIO_MULTISELECT) {
        this.AUDIO_MULTISELECT_EXIT();
      }

      this.SET_MODE({ name: 'VISUAL_DND', value: true });
      this.SET_DND_ESCORT({ name: 'isExternal', value: true });
      this.SET_DND_ESCORT({ name: 'track2replace', value: null });
      this.$data.showGhost = true;

      state.mousePoint = new TimePoint();
      state.aTimes = getVisualAnchorTimes(this.trackLayer.visual);
      state.proximityTester = this.$_createProximityTester(
        state.aTimes,
        this.CONST.AUTOSNAP_PROXIMITY.ASSET
      );
      this.$_defineOverlayModels(state);

      if (this.useSceneEdit) {
        state.semSensor = {
          leave: new ContinuousTimeout(this.SCENE_EDIT_TIMEOUT.LEAVE, () => {
            this.SCENE_EDIT_MODE_LEAVE({ keepItSelected: false }).then(() => {
              tmBus.$emit('callbackLeaveSceneEdit');
            });
          }),

          enter: new ContinuousTimeout(this.SCENE_EDIT_TIMEOUT.ENTER, () => {
            this.SCENE_EDIT_MODE_ENTER({
              track: state.track2replace,
              byHover: true,
            }).then(({ sceneTrack }) => {
              tmBus.$emit('callbackEnterSceneEdit', {
                asset: sceneTrack.data,
                autopilotEntering: true,
              });

              this.$_defineOverlayModels(state);
              this.$_onMove(state);
            });
          }),
        };
      }

      tmBus.$emit('callbackBeforeDragStarts');
      CursorManager.set('grabbing');
    },

    $_defineGhostMode(state) {
      if (this.MODE_SCENE_EDIT) {
        if (this.dndEscort.overlay2replace) {
          this.$data.mode = VisualGhostMode.REPLACE_OVERLAY;
        } else if (this.$data.isInside) {
          this.$data.mode = VisualGhostMode.DEFAULT_SCENE;
        } else {
          this.$data.mode = VisualGhostMode.DEFAULT;
        }
      } else if (null !== state.insertionTime) {
        const index = state.aTimes.indexOf(state.insertionTime);
        if (
          _.between(0, index, state.aTimes.length - 1, {
            includeMin: false,
            includeMax: false,
          })
        ) {
          this.$data.mode = VisualGhostMode.INSERT_BETWEEN;
        } else if (!state.outerSpace) {
          this.$data.mode = VisualGhostMode.INSERT;
        } else {
          this.$data.mode = null;
        }
      } else if (null !== state.track2replace) {
        this.$data.mode = this.useSceneEdit
          ? VisualGhostMode.REPLACE_SCENE
          : VisualGhostMode.REPLACE_DEFAULT;
      } else {
        this.$data.mode = VisualGhostMode.DEFAULT;
      }
    },

    $_defineOverlayModels(state) {
      if (this.MODE_SCENE_EDIT && !state.overlay) {
        state.overlay = createOverlayFromExternalAsset(this.$props.asset);
        state.initialOverlay = assignDurations({}, state.overlay);
      }
    },

    $_controlSemSensors(state) {
      if (this.MODE_SCENE_EDIT) {
        if (!this.sceneEscort.byHover) {
          // NOOP - don't leave if SEM is manual
        } else if (this.$data.isInside) {
          if (state.isInsideScene) {
            state.semSensor.leave.abort();
          } else {
            state.semSensor.leave.relevant();
          }
        } else {
          state.semSensor.leave.relevant();
        }
      } else {
        if (this.$data.isInside) {
          if (state.track2replace) {
            state.semSensor.enter.relevant();
          } else if (null !== state.insertionTime) {
            state.semSensor.enter.abort();
          }
        } else {
          state.semSensor.enter.abort();
        }
      }
    },

    $_onMove(state) {
      state.mousePoint.set(state.eMove.clientX, state.eMove.clientY);
      this.$data.isInside = this.axis.isInside(state.mousePoint);

      this.SET_DND_ESCORT({
        name: 'showVisualAnchors',
        value: this.$data.isInside && !this.MODE_SCENE_EDIT,
      });
      this.SET_DND_ESCORT({
        name: 'useScrollSensor',
        value: this.$data.isInside,
      });

      state.insertionTime = null;
      state.track2replace = null;

      if (this.MODE_SCENE_EDIT) {
        this.$_moveOverlay(state);
      } else {
        this.$_moveVisual(state);
      }

      this.$_defineGhostMode(state);
      if (this.useSceneEdit) {
        this.$_controlSemSensors(state);
      }
    },

    $_moveOverlay(state) {
      this.SET_MODE({ name: 'OVERLAY_DND', value: true });
      this.SET_DND_ESCORT({ name: 'insertionTime', value: null });
      this.SET_DND_ESCORT({ name: 'track2replace', value: null });
      this.SET_DND_ESCORT({ name: 'overlay2replace', value: null });

      this.sceneEscort.actingPipa.delete(state.overlay.guid);

      if (this.$data.isInside) {
        this.$_toInternal(state.mousePoint);
        this.$_point2time(state.mousePoint);
        const curTime = fg.trunc(state.mousePoint.time);
        const fitInPast = state.canFit;

        state.isInsideScene = isTimeInsideTrack(
          curTime,
          this.sceneEscort.sceneTrack
        );
        if (state.isInsideScene) {
          state.canFit = this.sceneEscort.actingPipa.canFit({
            overlay: state.overlay,
            absoluteTime: curTime,
          });

          if (state.canFit) {
            this.sceneEscort.actingPipa.insert({
              overlay: state.overlay,
              absoluteTime: curTime,
              isNew: true,
            });
          } else {
            const overlayBelow = this.sceneEscort.actingPipa.find({
              absoluteTime: curTime,
            });
            if (overlayBelow) {
              this.SET_DND_ESCORT({
                name: 'overlay2replace',
                value: overlayBelow,
              });
            }
          }
        } else {
          state.canFit = false;
        }

        this.$data.showGhost = !state.isInsideScene || !state.canFit;

        this.$_toExternal(state.mousePoint);
        this.$data.ghostPoint.assign(state.mousePoint);

        if (!fitInPast && state.canFit) {
          tmBus.$emit('callbackSceneOverlayInserted');
        }
      } else {
        this.$data.ghostPoint.assign(state.mousePoint);
        this.$data.showGhost = true;
        state.canFit = false;
      }
    },

    $_moveVisual(state) {
      if (this.$data.isInside) {
        this.$_toInternal(state.mousePoint);
        this.$_point2time(state.mousePoint);

        const searchResult = findVisualAnchorPoint(
          state.proximityTester,
          state.mousePoint.time
        );
        state.insertionTime = searchResult.time;
        state.outerSpace = searchResult.outerSpace;

        if (null === state.insertionTime) {
          state.track2replace = this.$_time2visualTrack(state.mousePoint.time);
        }

        this.$_toExternal(state.mousePoint);
      }

      this.$data.ghostPoint.assign(state.mousePoint);

      this.SET_DND_ESCORT({
        name: 'insertionTime',
        value: state.insertionTime,
      });
      this.SET_DND_ESCORT({
        name: 'track2replace',
        value: state.track2replace,
      });
    },

    async $_onMoveEnd(state) {
      CursorManager.restore();
      if (state.semSensor) {
        state.semSensor.enter.abort();
        state.semSensor.leave.abort();
      }

      if (state.canceled || !this.$data.isInside) {
        if (this.MODE_SCENE_EDIT && state.overlay) {
          this.sceneEscort.actingPipa.delete(state.overlay.guid);
          if (this.sceneEscort.byHover) {
            this.SCENE_EDIT_MODE_LEAVE({ keepItSelected: false }).then(() => {
              tmBus.$emit('callbackLeaveSceneEdit');
            });
          }
        }
      } else {
        let seekToTime = this.secondhand;

        if (this.MODE_SCENE_EDIT && state.overlay) {
          let insertedOverlay = this.sceneEscort.actingPipa.find({
            guid: state.overlay.guid,
          });

          if (this.dndEscort.overlay2replace) {
            this.sceneEscort.actingPipa.replaceOverlay({
              oldOverlay: this.dndEscort.overlay2replace,
              newOverlay: state.overlay,
            });
            insertedOverlay = this.sceneEscort.actingPipa.find({
              guid: state.overlay.guid,
            });
            tmBus.$emit('callbackSceneOverlayInserted');
          }

          if (insertedOverlay) {
            seekToTime = insertedOverlay.displayStartTime + 0.001;
            this.SELECT_OVERLAY({ overlay: insertedOverlay });
          }

          tmBus.$emit('callbackSceneUpdate', {
            scene: this.sceneEscort.actingPipa.scene,
            reflectUsage: true,
          });
          tmBus.$emit('callbackRCPSeek', {
            time: seekToTime,
            seekImmediate: false,
          });
        } else if (
          null !== state.insertionTime ||
          null !== state.track2replace
        ) {
          const asset = _.cloneDeep(this.$props.asset);
          // @note: asset mutates after this call
          tmBus.$emit('callbackBeforeAddingExternalAsset', {
            external: true,
            asset,
            replacedAsset: state.track2replace?.data,
          });
          let track = null;
          if (null !== state.insertionTime) {
            track = await this.INSERT_VISUAL_ASSET({
              time: state.insertionTime,
              asset,
            });
          } else if (null !== state.track2replace) {
            track = await this.REPLACE_VISUAL_TRACK({
              track2replace: state.track2replace,
              asset,
            });
          }
          seekToTime = track?.data.displayStartTime + 0.001;
          tmBus.$emit('callbackChange', {
            e: state.eUp,
            seekToTime,
          });

          tmBus.$emit('callbackAfterAddingExternalAsset', { track });
        }

        if (isNumber(seekToTime)) {
          this.SET_SECONDHAND({ time: seekToTime });
        }
      }

      if (this.MODE_SCENE_EDIT && this.sceneEscort.byHover) {
        // while in SEM - can enter by hover only once
        this.SET_SCENE_ESCORT({ name: 'byHover', value: false });
      }
      this.SET_DND_ESCORT({ name: 'showVisualAnchors', value: false });
      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: false });
      this.SET_DND_ESCORT({ name: 'insertionTime', value: null });
      this.SET_DND_ESCORT({ name: 'track2replace', value: null });
      this.SET_DND_ESCORT({ name: 'overlay2replace', value: null });
      this.SET_DND_ESCORT({ name: 'isExternal', value: false });
      this.SET_MODE({ name: 'VISUAL_DND', value: false });
      this.SET_MODE({ name: 'OVERLAY_DND', value: false });
      this.$data.showGhost = false;
    },
  },
};
